import Task20 from '@carbon/icons-react/es/task/20';
import { ColumnApi, GridApi, GridReadyEvent } from 'ag-grid-community';
import { RadioChangeEvent } from 'antd';
import { debounce, keyBy } from 'lodash';
import { pipe } from 'ramda';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation } from 'react-router';
import { OrderType } from 'api/models';
import { PageLoading } from 'components/atoms/loading';
import { HeaderWithIconTitle, Page } from 'components/molecules/page/page';
import { useCurrentView } from 'components/molecules/views/current-view-state';
import {
  asColumnOrder,
  cleanUpColumnOrder,
  ColumnOrder,
  GridChangeEvent,
} from 'components/organisms/grid-custom-column';
import { FilterableField } from 'components/organisms/tasks/fields';
import { useFilters } from 'components/organisms/tasks/use-filters';
import { ToggleState } from 'components/organisms/templates/projects/plan/table/actions';
import { from_search, to_query } from 'components/pages/filter-transformer';
import { RoutePaths } from 'constant';
import { useAccount } from 'hooks/use-account';
import {
  addUpdateColumnOrderIndexedDb,
  getColumnOrderFromIndexedDb,
} from 'hooks/use-idb';
import { useLocalStorage } from 'hooks/use-local-storage';
import { useProjectsMetaQuery, useProjectsQuery } from 'queries';
import { useProjectGridToUpdate } from 'queries/project';
import { useProjectFieldsAsFilterableFields } from 'utils/project-fields/use-project-fields-as-filterable-fields';
import { useProjectFieldsAccess } from 'utils/project-fields/use-project-native-fields-as-filterable-fields';
import { CreateProjectPage } from '../create';
import {
  CsatRatingField,
  LastMilestoneRatingField,
  PastProjectStatusField,
  ProjectHoursField,
  ProjectPhasesField,
} from './fields';
import { ProjectListFilterBar, useProjectListGroupByState } from './filter-bar';
import { ProjectViews } from './filter-bar/views';
import {
  withRowGroup,
  withOnUpdate,
  DEFAULT_COLUMN_ORDER,
  relativelySortByDefaultColumnOrder,
  getColDef,
} from './grid/columns';
import { ProjectListGrid } from './grid/grid';
import {
  PROJECT_LIST_COLUMNS_STATE_LOCALSTORAGE_KEY,
  useGridOptions,
} from './grid/grid-options';
import { Wrapper } from './styled';
import { transform } from './transformer';

export const computeRowData = ({ metaData, data, fieldLookupById }) => {
  let rowData;

  if (metaData) {
    const metaDataMap = keyBy(metaData, 'projectId');
    const projectsWithMetaData = data.map((project) => {
      const meta = metaDataMap[project.projectId];

      return {
        ...project,
        ...meta,
      };
    });

    rowData = transform(fieldLookupById)(projectsWithMetaData);
  } else {
    rowData = transform(fieldLookupById)(data);
  }

  return rowData;
};

const withValidCustomProjectFields = (
  accountId: number,
  field: FilterableField,
): FilterableField => {
  if (![121, 622, 617, 700, 635].includes(accountId)) {
    return field;
  }

  return {
    ...field,
    fieldName:
      field.customField &&
      field.fieldName.toLowerCase().includes('arr') &&
      !field.fieldName.toLowerCase().includes(field.fieldId.toString())
        ? `${field.fieldName}${field.fieldId}`
        : field.fieldName,
  };
};

const ProjectList: FC<{ allFields: FilterableField[] }> = ({ allFields }) => {
  const { accountId } = useAccount();
  const excludedProjectFields = useMemo(() => [''], []);
  const excludedProjectFieldsForFilter = useMemo(
    () => [...excludedProjectFields, 'estimatedDueDate'],
    [excludedProjectFields],
  );

  const hasAccessToField = useProjectFieldsAccess();
  const acceptedProjectFields = useCallback(
    (field: FilterableField) =>
      !excludedProjectFields.includes(field.fieldName) &&
      hasAccessToField(field),
    [excludedProjectFields, hasAccessToField],
  );

  const acceptedProjectFilterFields = useCallback(
    (field: FilterableField) =>
      !excludedProjectFieldsForFilter.includes(field.fieldName) &&
      hasAccessToField(field),
    [excludedProjectFieldsForFilter, hasAccessToField],
  );
  const { data } = useProjectsQuery();
  const { data: metaData } = useProjectsMetaQuery();
  const [searchValue, setSearchValue] = useState('');
  const [gridApi, setGridApi] = useState<GridApi | undefined>(undefined);
  const [gridColumnApi, setGridColumnApi] =
    useState<ColumnApi | undefined>(undefined);
  const [columnOrderV2] = useLocalStorage<ColumnOrder[]>(
    PROJECT_LIST_COLUMNS_STATE_LOCALSTORAGE_KEY,
    DEFAULT_COLUMN_ORDER,
  );
  const { viewId: currentViewId } = useCurrentView({
    defaultViewId: ProjectViews.AllProjects,
  });
  const masterColumnOrder = useMemo(
    () => columnOrderV2 || DEFAULT_COLUMN_ORDER,
    [columnOrderV2],
  );

  const [gridInitialized, setGridInitialized] = useState(false);
  const filterFields = useMemo(
    () => allFields.filter(acceptedProjectFilterFields),
    [allFields, acceptedProjectFilterFields],
  );

  const columnFields = useMemo(
    () =>
      [
        ...allFields,
        PastProjectStatusField,
        LastMilestoneRatingField,
        CsatRatingField,
        ProjectHoursField,
        ProjectPhasesField,
      ]
        .filter(acceptedProjectFields)
        .map((field) => withValidCustomProjectFields(accountId, field)),
    [allFields, acceptedProjectFields, accountId],
  );
  const colRef = useRef(columnFields);

  useEffect(() => {
    if (gridInitialized) {
      return;
    }
    colRef.current = columnFields;
  }, [columnFields, gridInitialized]);
  const fieldLookupById = useMemo(
    () => keyBy(columnFields, 'fieldId'),
    [columnFields],
  );

  const filterFieldsLookupByName = useMemo(
    (): Record<string, FilterableField> => keyBy(filterFields, 'fieldName'),
    [filterFields],
  );
  const [groupKey, setGroupKey] = useProjectListGroupByState(filterFields);

  const { filter, replaceFilter, selectFilter, clearFilters, filterFunction } =
    useFilters(filterFieldsLookupByName, 'loaded');

  const [initialized, setInitialized] = useState(false);
  const [query, setQuery] = useLocalStorage('project_list.filter', '');
  const { search: queryParams } = useLocation();

  const { mutateAsync } = useProjectGridToUpdate({
    onError({ project, meta }) {
      if (!project || !meta) {
        return;
      }
      const [oldProject] = transform(fieldLookupById)([
        { ...project, ...meta },
      ]);
      const row = gridApi?.getRowNode(`${oldProject.projectId}`);

      row?.updateData(oldProject);
    },
  });

  useEffect(() => {
    if (!initialized) {
      return;
    }

    setQuery(to_query(filter));
  }, [filter, initialized, setQuery]);

  useEffect(() => {
    if (initialized) {
      return;
    }
    const restore_query = (queryParams ?? '') === '' ? query : queryParams;
    const saved_filter = from_search(filterFieldsLookupByName)(restore_query);

    replaceFilter(saved_filter);
    setInitialized(true);
  }, [
    initialized,
    filterFieldsLookupByName,
    queryParams,
    replaceFilter,
    query,
  ]);

  const defaultColumnState: ColumnOrder[] = useMemo(() => {
    const toColumnDefs = pipe(getColDef, relativelySortByDefaultColumnOrder);

    return asColumnOrder(
      toColumnDefs(columnFields).map((x) => ({ ...x, colId: x.field })),
    );
  }, [columnFields]);

  const [order, setOrder] = useState(
    cleanUpColumnOrder(defaultColumnState)(masterColumnOrder),
  );

  useEffect(() => {
    async function getColOrder() {
      const lsOrder = JSON.parse(
        localStorage.getItem(
          `project_list.v3.viewId=${currentViewId}.column_state`,
        ) || '[]',
      );
      const idbData = await getColumnOrderFromIndexedDb(
        `project_list.v1.view=${currentViewId}`,
      );
      let _data =
        idbData && idbData.columnOrder.length > 0 ? idbData.columnOrder : [];

      if (lsOrder && lsOrder.length > 0) {
        _data = lsOrder;
        localStorage.removeItem(
          `project_list.v3.viewId=${currentViewId}.column_state`,
        );
      }

      if (_data && _data?.length > 0) {
        if (defaultColumnState && defaultColumnState.length > 0) {
          setOrder(cleanUpColumnOrder(defaultColumnState)(_data));
        } else {
          setOrder(_data);
        }
      }
    }
    getColOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentViewId]);

  const [columnOrder, setColumnOrder] = useState<ColumnOrder[]>(order);

  useEffect(() => {
    if (!gridApi || !columnFields.length) {
      return;
    }
    const toColumnDefs = pipe(
      getColDef,
      withOnUpdate(mutateAsync),
      withRowGroup(groupKey),
    );
    const columnDefs = toColumnDefs(columnFields);

    gridApi?.setColumnDefs(columnDefs);
  }, [columnFields, data, gridApi, groupKey, mutateAsync]);

  useEffect(() => {
    if (!gridApi || !data || !columnFields.length) {
      return;
    }
    if (gridInitialized) {
      return;
    }

    const rowData = computeRowData({
      metaData,
      data,
      fieldLookupById,
    });

    gridApi.setRowData(rowData);
    gridApi.refreshClientSideRowModel();
    if (metaData?.length) {
      setGridInitialized(true);
    }
  }, [
    columnFields.length,
    data,
    fieldLookupById,
    gridApi,
    gridInitialized,
    metaData,
  ]);

  const onGridReady = useCallback((event: GridReadyEvent) => {
    setGridApi(event.api);
    setGridColumnApi(event.columnApi);
  }, []);

  useEffect(() => {
    if (gridApi) {
      gridApi.refreshHeader();
      if (gridApi.getDisplayedRowCount() < 1) {
        gridApi.showNoRowsOverlay();
      } else {
        gridApi.hideOverlay();
      }
    }
  }, [gridApi, searchValue, filter]);

  const blockEffect = useRef(false);

  const handleColumnOrderChangedByView = useCallback(
    (changedColumnOrder: ColumnOrder[]) => {
      blockEffect.current = true;
      setColumnOrder(
        cleanUpColumnOrder(defaultColumnState)(changedColumnOrder),
      );
    },
    [defaultColumnState],
  );

  useEffect(() => {
    if (blockEffect.current) {
      return;
    }
    // This useEffect is only to run for first page load
    setColumnOrder(order);
  }, [currentViewId, order]);

  useEffect(() => {
    // Used to update the grid with latest columnOrder
    if (!gridColumnApi) {
      return;
    }
    gridColumnApi?.setColumnState(columnOrder);
  }, [columnOrder, gridColumnApi]);

  useEffect(() => {
    async function addUpdateColOrder() {
      await addUpdateColumnOrderIndexedDb(
        `project_list.v1.view=${currentViewId}`,
        currentViewId,
        order,
        OrderType.ProjectList,
      );
    }
    addUpdateColOrder();
  }, [currentViewId, order]);

  // Called whenever grid is altered to update columnOrder state and LocalStorage
  const handleGridChange = useCallback(
    (event: GridChangeEvent) => {
      if (event) {
        const newColumnOrder = asColumnOrder(event.columnApi.getColumnState());

        setColumnOrder(newColumnOrder);
        setOrder(newColumnOrder);
      }
    },
    [setOrder],
  );

  const throttledGridChange = useMemo(
    () => debounce(handleGridChange, 100),
    [handleGridChange],
  );

  const gridOptions = useGridOptions(colRef.current, throttledGridChange);
  const [mode, setMode] = useState<ToggleState>('expand');

  const onCollapseChange = (e: RadioChangeEvent) => {
    const toggleMode = e.target.value as ToggleState;

    switch (toggleMode) {
      case 'collapse':
        gridApi?.collapseAll();
        setMode(toggleMode);
        break;
      case 'expand':
        gridApi?.expandAll();
        setMode(toggleMode);
        break;
      default:
    }
  };

  useEffect(() => {
    setMode('expand');
  }, [groupKey]);

  const searchFilter = useCallback(
    (node) => {
      return (
        node?.projectName.toLowerCase().includes(searchValue.toLowerCase()) ??
        true
      );
    },
    [searchValue],
  );

  return (
    <Page.Content>
      <ProjectListFilterBar
        filter={filter}
        clearFilters={clearFilters}
        selectFilter={selectFilter}
        searchValue={searchValue}
        onSearchInputChange={(e) => setSearchValue(e.target.value)}
        allFields={allFields}
        columnFields={columnFields}
        groupKey={groupKey}
        setGroupKey={setGroupKey}
        columnOrder={columnOrder}
        defaultColumnOrder={defaultColumnState}
        handleColumnOrderChange={handleColumnOrderChangedByView}
        onCollapseChange={onCollapseChange}
        mode={mode}
        gridColumnApi={gridColumnApi}
        masterColumnOrder={masterColumnOrder}
      />
      <Wrapper className="ag-theme-balham">
        <ProjectListGrid
          {...gridOptions}
          size="large"
          onGridReady={onGridReady}
          animateRows
          disableStaticMarkup
          filter={filterFunction ?? undefined}
          search={searchValue ? searchFilter : undefined}
        />
      </Wrapper>
    </Page.Content>
  );
};

export const ProjectListV2Page: FC = () => {
  const { fields: allFields, loading: isFieldsLoading } =
    useProjectFieldsAsFilterableFields();
  const { pathname } = useLocation();
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);

  useEffect(() => {
    if (pathname === RoutePaths.projectCreate) {
      setShowCreateModal(true);
    } else {
      setShowCreateModal(false);
    }
  }, [pathname]);

  return (
    <Page $gray>
      <Page.Header
        sticky
        compact
        title={
          <HeaderWithIconTitle
            icon={<Task20 />}
            title={<FormattedMessage id="projects.title" />}
          />
        }
      />
      {isFieldsLoading ? (
        <PageLoading />
      ) : (
        <ProjectList allFields={allFields} />
      )}
      {showCreateModal ? <CreateProjectPage /> : null}
    </Page>
  );
};
